import { useCallback, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import useSubscriptionStatus from "./useSubscriptionStatus";
import SubscriptionRequiredModal from "../components/subscriptions/SubscriptionRequiredModal";

/**
 * Reusable guard for actions that require a subscription specifically to
 * connect/setup/configure smart locks.
 *
 * - Does not create any new Stripe sessions or billing logic.
 * - Navigates to the existing subscription page (`/pricing`) on Continue.
 * - Preserves back navigation by passing { from } in router state.
 */
const useSmartLockSubscriptionGuard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isSubscribed, loading } = useSubscriptionStatus();
  const [modalOpen, setModalOpen] = useState(false);

  const requireSubscription = useCallback(
    (onAllowed) => {
      if (loading) return;
      if (isSubscribed) {
        onAllowed?.();
      } else {
        setModalOpen(true);
      }
    },
    [isSubscribed, loading]
  );

  const onCancel = useCallback(() => setModalOpen(false), []);

  const onContinue = useCallback(() => {
    setModalOpen(false);
    const from = `${location.pathname}${location.search || ""}`;
    navigate("/pricing", { state: { from } });
  }, [location.pathname, location.search, navigate]);

  const modal = useMemo(
    () => (
      <SubscriptionRequiredModal
        open={modalOpen}
        onContinue={onContinue}
        onCancel={onCancel}
      />
    ),
    [modalOpen, onContinue, onCancel]
  );

  return { requireSubscription, subscriptionGuardLoading: loading, modal };
};

export default useSmartLockSubscriptionGuard;

